import { styled } from '@mui/material'

import { theme } from 'styles/theme'

const { neutral, primary } = theme.palette

/**
 * Casca do menu dentro do Popover. Largura fixa e coluna; o header e a área de
 * scroll são filhos diretos.
 */
export const MenuContainer = styled('div')(() => ({
  width: 320,
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'Inter, sans-serif'
}))

/**
 * Área rolável das categorias. Impede que a lista (ex.: muitos campos extras)
 * vaze pra fora da viewport.
 */
export const ScrollArea = styled('div')(() => ({
  maxHeight: 'min(60vh, 460px)',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '4px 0',

  '&::-webkit-scrollbar': {
    width: 6
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: neutral[600],
    borderRadius: 3
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent'
  }
}))

/** Divisor sutil entre categorias. */
export const CategoryDivider = styled('div')(() => ({
  height: 1,
  backgroundColor: neutral[800],
  margin: '4px 16px'
}))

/**
 * Cabeçalho clicável de uma categoria: ícone + rótulo forte + chevron.
 * Reset de botão nativo pra herdar o tratamento de foco/hover do tema.
 */
export const CategoryButton = styled('button')<{ $expanded: boolean }>(
  ({ $expanded }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    height: 44,
    padding: '0 16px',
    cursor: 'pointer',
    backgroundColor: $expanded ? neutral[900] : 'transparent',
    transition: 'background-color 120ms ease',

    '&:hover': {
      backgroundColor: neutral[800]
    },
    '&:focus-visible': {
      backgroundColor: neutral[800],
      outline: `2px solid ${primary[200]}`,
      outlineOffset: -2
    }
  })
)

export const CategoryIcon = styled('span')(() => ({
  display: 'inline-flex',
  color: neutral[300],
  flexShrink: 0
}))

/** Chevron que gira ao expandir. */
export const Chevron = styled('span')<{ $expanded: boolean }>(
  ({ $expanded }) => ({
    display: 'inline-flex',
    marginLeft: 'auto',
    color: neutral[400],
    transition: 'transform 160ms ease',
    transform: $expanded ? 'rotate(90deg)' : 'rotate(0deg)'
  })
)

/**
 * Item selecionável de uma categoria. Indentado pra alinhar sob o rótulo (além
 * do ícone), 1 linha com reticências pra labels longos.
 */
export const ItemButton = styled('button')<{ $disabled: boolean }>(
  ({ $disabled }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minHeight: 40,
    padding: '8px 16px 8px 46px',
    cursor: $disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 120ms ease',

    '&:hover': {
      backgroundColor: $disabled ? 'transparent' : neutral[800]
    },
    '&:focus-visible': {
      backgroundColor: neutral[800],
      outline: `2px solid ${primary[200]}`,
      outlineOffset: -2
    }
  })
)

/** Rótulo do item: 1 linha, reticências no overflow. */
export const ItemLabel = styled('span')<{ $disabled: boolean }>(
  ({ $disabled }) => ({
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '120%',
    color: $disabled ? neutral[500] : neutral[300],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })
)

/** Selo "Adicionado" pro item já presente na descrição. */
export const AddedTag = styled('span')(() => ({
  flexShrink: 0,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '120%',
  color: neutral[400]
}))

/** Estado de carregando / vazio dentro de uma categoria. */
export const StateMessage = styled('div')(() => ({
  padding: '8px 16px 8px 46px',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '120%',
  color: neutral[400]
}))
